<script setup>
import { computed, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useRouter } from 'vue-router';
import { useAlert } from 'dashboard/composables';
import { picoSearch } from '@chatwoot/pico-search';

import Button from 'dashboard/components-next/button/Button.vue';
import SettingsLayout from 'dashboard/routes/dashboard/settings/SettingsLayout.vue';
import BaseSettingsHeader from 'dashboard/routes/dashboard/settings/components/BaseSettingsHeader.vue';
import AssignmentPolicyCard from 'dashboard/components-next/AssignmentPolicy/AssignmentPolicyCard/AssignmentPolicyCard.vue';
import ConfirmDeletePolicyDialog from './components/ConfirmDeletePolicyDialog.vue';

const store = useStore();
const { t } = useI18n();
const router = useRouter();

const agentAssignmentsPolicies = useMapGetter(
  'assignmentPolicies/getAssignmentPolicies'
);
const uiFlags = useMapGetter('assignmentPolicies/getUIFlags');
const inboxUiFlags = useMapGetter('assignmentPolicies/getInboxUiFlags');

const confirmDeletePolicyDialogRef = ref(null);
const searchQuery = ref('');

const filteredPolicies = computed(() => {
  const query = searchQuery.value.trim();
  if (!query) return agentAssignmentsPolicies.value;
  return picoSearch(agentAssignmentsPolicies.value, query, [
    'name',
    'description',
  ]);
});

const onClickCreatePolicy = () => {
  router.push({
    name: 'agent_assignment_policy_create',
  });
};

const onClickEditPolicy = id => {
  router.push({
    name: 'agent_assignment_policy_edit',
    params: {
      id,
    },
  });
};

const handleFetchInboxes = id => {
  if (inboxUiFlags.value.isFetching) return;
  store.dispatch('assignmentPolicies/getInboxes', id);
};

const handleDelete = id => {
  confirmDeletePolicyDialogRef.value.openDialog(id);
};

const handleDeletePolicy = async policyId => {
  try {
    await store.dispatch('assignmentPolicies/delete', policyId);
    useAlert(
      t(
        'ASSIGNMENT_POLICY.AGENT_ASSIGNMENT_POLICY.DELETE_POLICY.SUCCESS_MESSAGE'
      )
    );
    confirmDeletePolicyDialogRef.value.closeDialog();
  } catch (error) {
    useAlert(
      t('ASSIGNMENT_POLICY.AGENT_ASSIGNMENT_POLICY.DELETE_POLICY.ERROR_MESSAGE')
    );
  }
};

onMounted(() => {
  store.dispatch('assignmentPolicies/get');
});
</script>

<template>
  <SettingsLayout
    :is-loading="uiFlags.isFetching"
    :no-records-found="agentAssignmentsPolicies.length === 0"
    :no-records-message="
      $t('ASSIGNMENT_POLICY.AGENT_ASSIGNMENT_POLICY.INDEX.NO_RECORDS_FOUND')
    "
  >
    <template #header>
      <BaseSettingsHeader
        v-model:search-query="searchQuery"
        :title="$t('ASSIGNMENT_POLICY.INDEX.HEADER.TITLE')"
        :description="$t('ASSIGNMENT_POLICY.INDEX.HEADER.DESCRIPTION')"
        :search-placeholder="
          $t(
            'ASSIGNMENT_POLICY.AGENT_ASSIGNMENT_POLICY.INDEX.SEARCH_PLACEHOLDER'
          )
        "
        feature-name="assignment-policy"
      >
        <template v-if="agentAssignmentsPolicies?.length" #count>
          <span class="text-body-main text-n-slate-11">
            {{
              $t('ASSIGNMENT_POLICY.AGENT_ASSIGNMENT_POLICY.INDEX.COUNT', {
                n: agentAssignmentsPolicies.length,
              })
            }}
          </span>
        </template>
        <template #actions>
          <Button
            :label="
              $t(
                'ASSIGNMENT_POLICY.AGENT_ASSIGNMENT_POLICY.INDEX.HEADER.CREATE_POLICY'
              )
            "
            size="sm"
            icon="i-lucide-plus"
            @click="onClickCreatePolicy"
          />
        </template>
      </BaseSettingsHeader>
    </template>
    <template #body>
      <div class="flex flex-col gap-4 pt-4">
        <AssignmentPolicyCard
          v-for="policy in filteredPolicies"
          :key="policy.id"
          v-bind="policy"
          :is-fetching-inboxes="inboxUiFlags.isFetching"
          @fetch-inboxes="handleFetchInboxes"
          @edit="onClickEditPolicy"
          @delete="handleDelete"
        />
      </div>
    </template>
    <ConfirmDeletePolicyDialog
      ref="confirmDeletePolicyDialogRef"
      @delete="handleDeletePolicy"
    />
  </SettingsLayout>
</template>
